import React, { ComponentProps, MouseEventHandler, PropsWithChildren, RefObject, useCallback } from 'react'
import DropdownListBody from './DropdownListBody'
import DropdownListFooter from './DropdownListFooter'
import DropdownListHeader from './DropdownListHeader'
import DropdownListBase from './DropdownListBase'
import { _FloatingDropdownCloseHandler } from '../_FloatingDropdown'

type InheritedHeaderProps = Pick<ComponentProps<typeof DropdownListHeader>, 'title' | 'subtitle'>
type InheritedFooterProps = Pick<ComponentProps<typeof DropdownListFooter>, 'primaryActionProps' | 'isPrimaryActionHidden' | 'secondaryActionProps' | 'isSecondaryActionHidden'>
type InheritedBaseProps = Pick<ComponentProps<typeof DropdownListBase>, 'size' | 'width' | 'anchorRef' | 'anchorOffset' | 'boundaryRef' | 'triggerRef' | 'size' | 'placement' | 'enableFlip' | 'open' | 'portaled' | 'id' | 'crossAxisShift' | 'autoHeight'>

export type DropdownListCloseHandler = _FloatingDropdownCloseHandler

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes, InheritedHeaderProps, InheritedFooterProps, InheritedBaseProps {
  hasDismissButton?: boolean
  /**
   * @default S
   */
  headerSize?: ComponentProps<typeof DropdownListHeader>['size']
  headerExtension?: ComponentProps<typeof DropdownListHeader>['extensionSlot']
  /**
   * @default default
   */
  footerActionsLayout?: ComponentProps<typeof DropdownListFooter>['actionsLayout']
  /**
   * Reference to the body element wrapping the main content.
   */
  bodyRef?: RefObject<HTMLDivElement | null>
  onClose?: DropdownListCloseHandler
  ref?: React.Ref<HTMLDivElement | null>
}

function DropdownList(props: Props) {
  const {
    children,
    size = 'M',
    width,
    hasDismissButton,
    headerSize = 'S',
    subtitle,
    title,
    headerExtension,
    primaryActionProps,
    isPrimaryActionHidden,
    secondaryActionProps,
    isSecondaryActionHidden,
    footerActionsLayout,
    bodyRef,
    anchorRef,
    anchorOffset,
    boundaryRef,
    triggerRef,
    open,
    placement,
    enableFlip,
    portaled,
    crossAxisShift,
    autoHeight,
    onClose,
    id,
    ref,
    ...rest
  } = props

  const handleDismissClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    onClose?.(event, 'dismiss-click')
  }, [onClose])

  const hasHeader = !!(title || subtitle || headerExtension || hasDismissButton)
  const hasPrimaryAction = !isPrimaryActionHidden && !!primaryActionProps?.children
  const hasSecondaryAction = !isSecondaryActionHidden && !!secondaryActionProps?.children
  const hasFooter = hasPrimaryAction || hasSecondaryAction

  return <DropdownListBase
    {...rest}
    ref={ref}
    anchorRef={anchorRef}
    anchorOffset={anchorOffset}
    boundaryRef={boundaryRef}
    triggerRef={triggerRef}
    open={open}
    size={size}
    width={width}
    placement={placement}
    enableFlip={enableFlip}
    id={id}
    onClose={onClose}
    portaled={portaled}
    crossAxisShift={crossAxisShift}
    autoHeight={autoHeight}
  >
    <DropdownListBody ref={bodyRef}>
      {children}
    </DropdownListBody>
    {hasHeader && <DropdownListHeader
      size={headerSize}
      subtitle={subtitle}
      title={title}
      extensionSlot={headerExtension}
      onDismissClick={hasDismissButton ? handleDismissClick : undefined}
    />}
    {hasFooter && <DropdownListFooter
      primaryActionProps={primaryActionProps}
      isPrimaryActionHidden={isPrimaryActionHidden}
      secondaryActionProps={secondaryActionProps}
      isSecondaryActionHidden={isSecondaryActionHidden}
      actionsLayout={footerActionsLayout}
    />}
  </DropdownListBase>
}

DropdownList.displayName = 'DropdownList'

export default DropdownList
